import { useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table, Nav
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { CABLE_NAMES_NO_ROPE, CABLE_COLORS } from '../../data/cableProducts.js';
import {
  buildCableModel,
  discArea,
  realWallToStandard
} from '../../helpers/cableGeometry.js';
import { putSide, putDiameter, activeEdits, isPinned } from '../../helpers/wallOverrides.js';
import ConductorIsolation3D from '../../components/standard/ConductorIsolation3D.js';
import Rope3D from '../../components/standard/Rope3D.js';
import SheathLayer3D from '../../components/standard/SheathLayer3D.js';
import Armour3D from '../../components/standard/Armour3D.js';
import MaterialTotalsChart from '../../components/charts/materials-charts/MaterialTotalsChart.js';
import { API } from '../../config/api';

const STD_API = `${API.BACKEND}/api/v1/standard/find-many`;
const toNum = v => {
  const n = Number(String(v ?? '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};
const f2 = v => (Number.isFinite(Number(v)) ? Number(v).toFixed(2) : '—');
const upper = v => String(v ?? '').trim().toUpperCase();

const PHASE_LABEL = {
  conductor_isolation: 'Conductor isolation',
  rope: 'Rope (stranding)',
  interior_sheat: 'Inner sheath',
  armouring: 'Armouring',
  exterior_sheat: 'Outer sheath'
};

async function fetchStandard(id) {
  const payload = [{
    name: upper(id.name), numberOfConductors: Number(id.numberOfConductors),
    section: Number(id.section), color: upper(id.color),
    hasMixedConductorSections: !!id.mixed, greenYellowSection: id.mixed ? Number(id.gy) : null
  }];
  const res = await axios.post(STD_API, payload);
  const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
  return data.find(s =>
    upper(s.name) === upper(id.name) && Number(s.numberOfConductors) === Number(id.numberOfConductors) &&
    Number(s.section) === Number(id.section) && upper(s.color) === upper(id.color)) || data[0] || null;
}

const emptyForm = () => ({
  name: 'CYABYF', numberOfConductors: 3, section: 2.5, color: 'GREY',
  mixed: false, gy: '', length: 1000, layLength: 100
});

// material(s) consumed AT a given phase, for a built model at `len` metres
function phaseConsumption(model, key, len) {
  if (!model) return [];
  if (key === 'conductor_isolation') {
    const ins = model.cores.reduce((s, c) => s + (c.ringArea * c.isoDensity / 1000) * len * model.takeUp, 0);
    return [
      { label: `Copper (${model.numberOfConductors}×)`, kg: model.totals.copperKg },
      { label: 'Insulation PVC', kg: ins }
    ];
  }
  if (key === 'rope') return [];
  if (key === 'armouring') {
    const L = model.cableLayers.find(x => x.key === 'armouring');
    const b = model.lines.find(x => x.group === 'BLR');
    return [{ label: L ? `BLR ${L.wide} x ${L.thickness}` : 'BLR', kg: b ? b.kg : 0 }];
  }
  const L = model.cableLayers.find(x => x.key === key);
  if (L) return [{ label: `${L.type || L.material} sheath`, kg: (L.ringArea * L.density / 1000) * len }];
  return [];
}

const StatRow = ({ label, value, hint }) => (
  <div className="d-flex justify-content-between border-bottom py-1 fs-9">
    <span className="text-700">{label}</span>
    <span className="fw-bold">{value}{hint && <span className="text-700 fw-normal ms-1">{hint}</span>}</span>
  </div>
);

// side-by-side Standard vs Current stat table (Current turns red when it differs)
const StatTable = ({ rows }) => (
  <Table size="sm" className="fs-9 mb-0">
    <thead>
      <tr className="text-700">
        <th>Field</th>
        <th className="text-end">Standard</th>
        <th className="text-end">Current</th>
      </tr>
    </thead>
    <tbody>
      {rows.map(r => {
        const changed = Math.abs(Number(r.cur) - Number(r.std)) > 0.005;
        return (
          <tr key={r.label}>
            <td className="text-700">{r.label}</td>
            <td className="text-end">{f2(r.std)}</td>
            <td className={`text-end fw-bold ${changed ? 'text-danger' : ''}`}>{f2(r.cur)}</td>
          </tr>
        );
      })}
    </tbody>
  </Table>
);

// four independent radial walls (top/right/bottom/left) for a layer
const SideInputs = ({ ov, onSet, nominal }) => (
  <Row className="g-1 mb-2">
    {['top', 'right', 'bottom', 'left'].map(s => (
      <Col xs={3} key={s}>
        <Form.Label className="fs-10 mb-0 text-capitalize">{s}</Form.Label>
        <Form.Control size="sm" className="no-spin" placeholder={nominal}
          value={ov?.[s] ?? ''} onChange={e => onSet(s, e.target.value)} />
      </Col>
    ))}
  </Row>
);

// What the radial would have to be for this layer to still reach the diameter
// the standard declares.
//
// The geometry is delta-anchored: an edit upstream moves this layer's inner Ø
// and the layer keeps its wall, so its outer Ø moves with it. That is what an
// extruder does. But the standard declares an outer diameter, and once the
// inner has moved the layer no longer reaches it — silently, because the four
// side boxes go on showing the wall from the standard.
//
// Shown, not applied: only the person planning knows whether the intent was to
// hold the wall or to hold the diameter.
const RealRadialNote = ({ layer }) => {
  const sq = realWallToStandard(layer);
  if (!sq) return null;

  return (
    <div className="fs-10 text-danger mb-2">
      {sq.impossible ? (
        <>
          Cannot reach the standard&apos;s Ø{f2(layer.storedOuter)} — the inner is already
          Ø{f2(layer.innerD)}, wider than the whole layer.
        </>
      ) : (
        <>
          Real radial to still reach Ø{f2(layer.storedOuter)}:{' '}
          <b>{sq.real.toFixed(3)} mm</b> per side{' '}
          <span className="text-700">
            (standard {sq.nominal.toFixed(3)}, {sq.delta > 0 ? '+' : ''}
            {sq.delta.toFixed(3)}) — the wall above is held, so the Ø moved to{' '}
            {f2(layer.outerD)} instead.
          </span>
        </>
      )}
    </div>
  );
};

// Outer Ø — a PIN, not a readout.
//
// The engine takes an explicit diameter over the four radial sides
// (`dOverride > 0 ? dOverride : innerD + 2 * wall`). This box used to render the
// live Ø as its `value`, so clicking in and typing — even retyping the very same
// number — pinned the geometry silently and permanently: the 3D went on
// following the per-side walls while Ø, section and consumption sat frozen, and
// only another Ø edit would move them.
//
// Empty now means "follow the walls" and the live figure is the placeholder —
// the same convention the side inputs above already use, so a box with text in
// it is exactly the set of things being forced.
const DiameterInput = ({ label, value, live, onSet, follows = 'the walls' }) => {
  const pinned = isPinned(value);
  return (
    <Row className="g-1 mb-2 align-items-end">
      <Col xs={6}>
        <Form.Label className="fs-10 mb-0">{label}</Form.Label>
        <Form.Control size="sm" className="no-spin" placeholder={live}
          value={value ?? ''} onChange={e => onSet(e.target.value)} />
      </Col>
      <Col xs={6} className="fs-10 pb-1">
        {pinned ? (
          <>
            <Badge bg="warning" text="dark">Ø forced</Badge>
            <Button variant="link" size="sm" className="fs-10 p-0 ms-2 align-baseline"
              onClick={() => onSet('')}>follow {follows}</Button>
          </>
        ) : (
          <span className="text-700">follows {follows} · now {live} mm</span>
        )}
      </Col>
    </Row>
  );
};


const StandardAnalytics = () => {
  const [form, setForm] = useState(emptyForm());
  const [items, setItems] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [selectedPhase, setSelectedPhase] = useState('conductor_isolation');
  const [wallOverrides, setWallOverrides] = useState({}); // {layerKey:{top,right,bottom,left}}
  const [armourLay, setArmourLay] = useState(40);
  const [interstitial, setInterstitial] = useState(false); // inner sheath fills the voids

  const addProduct = async () => {
    setError('');
    if (!form.name || !form.numberOfConductors || !form.section || !form.color) return;
    setLoading(true);
    try {
      const std = await fetchStandard(form);
      if (!std) { setError('Standard not found for that identity.'); return; }
      const id = `${Date.now()}-${items.length}`;
      // default lay length = rope Ø × 39 (a realistic lay ratio)
      const ropePhase = (std.phases || []).find(p => String(p.name).toLowerCase() === 'rope');
      const ropeD = ropePhase ? Number(ropePhase.diameter) : 0;
      const layLength = ropeD > 0 ? Math.round(ropeD * 39) : toNum(form.layLength);
      setItems(prev => [...prev, {
        id, std, name: std.name, numberOfConductors: std.numberOfConductors, section: std.section,
        color: std.color, mixed: !!form.mixed, gy: form.gy,
        length: toNum(form.length), layLength
      }]);
      setActiveId(id);
      setWallOverrides({});
    } catch (e) {
      setError('Fetch failed: ' + (e?.response?.data?.message || e.message));
    } finally { setLoading(false); }
  };

  const removeItem = id => setItems(prev => {
    const next = prev.filter(i => i.id !== id);
    if (activeId === id) { setActiveId(next[0]?.id || null); setWallOverrides({}); }
    return next;
  });
  const patchItem = (id, patch) => setItems(prev => prev.map(i => (i.id === id ? { ...i, ...patch } : i)));

  const active = items.find(i => i.id === activeId) || null;

  // A field typed into and then emptied again is not an edit. Keying off raw
  // `wallOverrides` left the "walls edited" badge and the Reset button on after
  // the last value had been cleared, and put the model in 'custom' when nothing
  // was overridden.
  const edits = useMemo(() => activeEdits(wallOverrides), [wallOverrides]);

  const hasEdits = Object.keys(edits).length > 0 || interstitial;
  const mode = Object.keys(edits).length > 0 ? 'custom' : 'nominal';

  const activeModel = useMemo(
    () => active ? buildCableModel(active.std, {
      length: active.length, layLength: active.layLength, wallMode: mode, wallOverrides: edits, interstitial
    }) : null,
    [active, mode, edits, interstitial]
  );

  // always-nominal model = the standard's own dimensions (ground truth), for the
  // "consumption for this phase, straight from the standard" readout
  const nominalModel = useMemo(
    () => active ? buildCableModel(active.std, {
      length: active.length, layLength: active.layLength, wallMode: 'nominal'
    }) : null,
    [active]
  );

  // the sheath applied directly over the rope (bedding) — the one that fills the
  // interstitial voids. interior_sheat if present, else exterior_sheat (MYYM).
  const beddingKey = useMemo(() => {
    if (!nominalModel?.hasRope) return null;
    const L = nominalModel.cableLayers.find(
      x => x.kind === 'sheath' && Math.abs(x.innerD - nominalModel.ropePackingD) < 0.3
    );
    return L?.key || null;
  }, [nominalModel]);

  // phases present in this cable
  const phaseTabs = useMemo(() => {
    if (!activeModel) return [];
    const t = [{ key: 'conductor_isolation' }];
    if (activeModel.hasRope) t.push({ key: 'rope' });
    for (const L of activeModel.cableLayers) t.push({ key: L.key });
    return t.map(x => ({ ...x, label: PHASE_LABEL[x.key] || x.key }));
  }, [activeModel]);

  const curPhase = phaseTabs.find(p => p.key === selectedPhase) ? selectedPhase : 'conductor_isolation';

  // set the same radial on all conductor cores
  const setCoreRadial = (side, val) => {
    const keys = (activeModel?.cores || []).map((_, i) => `iso_${i}`);
    setWallOverrides(o => {
      const next = { ...o };
      for (const k of keys) next[k] = putSide(next[k], side, val);
      return next;
    });
  };
  const setLayerRadial = (key, side, val) =>
    setWallOverrides(o => ({ ...o, [key]: putSide(o[key], side, val) }));

  // direct outer-Ø override — wins over the 4 radial sides, so editing a side
  // releases it (see putSide)
  const setCoreDiameter = val => {
    const keys = (activeModel?.cores || []).map((_, i) => `iso_${i}`);
    setWallOverrides(o => {
      const next = { ...o };
      for (const k of keys) next[k] = putDiameter(next[k], val);
      return next;
    });
  };
  const setLayerDiameter = (key, val) =>
    setWallOverrides(o => ({ ...o, [key]: putDiameter(o[key], val) }));
  const setRopeDiameter = val =>
    setWallOverrides(o => ({ ...o, rope: putDiameter(o.rope, val) }));

  // comparison across products (always nominal)
  const compareRows = useMemo(
    () => items.map(it => ({ it, m: buildCableModel(it.std, { length: it.length, layLength: it.layLength, wallMode: 'nominal' }) })),
    [items]
  );
  const compareChartData = useMemo(() => {
    const d = {};
    for (const { it, m } of compareRows) d[`${it.name} ${it.numberOfConductors}x${it.section}${it.std?.greenYellowSection ? `+${it.std.greenYellowSection}` : ''} @${it.length}m`] = Number(m.totals.totalKg.toFixed(1));
    return d;
  }, [compareRows]);

  // ── per-phase render + editor ──────────────────────────────────────────
  const renderPhase = () => {
    if (!activeModel) return null;
    const len = active.length;

    if (curPhase === 'conductor_isolation') {
      const c0 = activeModel.cores[0];
      const insKg = activeModel.cores.reduce((s, c) => s + (c.ringArea * c.isoDensity / 1000) * len * activeModel.takeUp, 0);
      return (
        <Row className="g-3">
          <Col lg={7}>
            <ConductorIsolation3D model={activeModel} />
          </Col>
          <Col lg={5}>
            <Card className="h-100">
              <Card.Header className="py-2 fw-bold">Conductor isolation</Card.Header>
              <Card.Body className="py-2">
                <div className="fs-10 text-700 mb-1">Radial wall on each side (mm). Ø, section &amp; consumption update live; leave blank for nominal.</div>
                <SideInputs ov={wallOverrides.iso_0} onSet={setCoreRadial} nominal={f2(c0.wall.nominal)} />
                <DiameterInput label="Insulated Ø (mm) — force a value"
                  value={wallOverrides.iso_0?.diameter} live={f2(c0.outerD)}
                  onSet={setCoreDiameter} />
                {(() => {
                  const nc0 = nominalModel.cores[0];
                  const nomIns = nominalModel.cores.reduce((s, c) => s + (c.ringArea * c.isoDensity / 1000) * active.length * nominalModel.takeUp, 0);
                  return (
                    <StatTable rows={[
                      { label: 'Conductor Ø (mm)', std: nc0.copperD, cur: c0.copperD },
                      { label: 'Wall avg (mm)', std: nc0.wall.avg, cur: c0.wall.avg },
                      { label: 'Insulated Ø (mm)', std: nc0.outerD, cur: c0.outerD },
                      { label: 'Section (mm²)', std: nc0.coreSection, cur: c0.coreSection },
                      { label: 'Insulation area/core (mm²)', std: nc0.ringArea, cur: c0.ringArea },
                      { label: `Copper (kg, ${activeModel.numberOfConductors}×)`, std: nominalModel.totals.copperKg, cur: activeModel.totals.copperKg },
                      { label: 'Insulation (kg)', std: nomIns, cur: insKg }
                    ]} />
                  );
                })()}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      );
    }

    if (curPhase === 'rope') {
      const extraCopper = activeModel.totals.copperKg * (1 - 1 / activeModel.takeUp);
      return (
        <Row className="g-3">
          <Col lg={7}><Rope3D model={activeModel} /></Col>
          <Col lg={5}>
            <Card className="h-100">
              <Card.Header className="py-2 fw-bold">Rope — stranding</Card.Header>
              <Card.Body className="py-2">
                <Form.Label className="fs-10 mb-0">
                  Lay length / pas: <b>{active.layLength} mm</b> <span className="text-700">(≈ Ø×{(active.layLength / activeModel.ropeDiameter).toFixed(0)})</span>
                </Form.Label>
                <Form.Range
                  min={Math.round(activeModel.ropeDiameter * 5)}
                  max={Math.round(activeModel.ropeDiameter * 70)}
                  step={1} value={active.layLength}
                  onChange={e => patchItem(active.id, { layLength: toNum(e.target.value) })} />
                <div className="fs-10 text-700 mb-2">Tighter pas → bigger lay angle → the rope Ø grows a little (helical bulge). Default = Ø×39.</div>
                <DiameterInput label="Rope Ø (mm) — force a value"
                  value={wallOverrides.rope?.diameter} live={f2(activeModel.ropeDiameter)}
                  onSet={setRopeDiameter} follows="packing + lay" />
                <StatRow label="Conductors" value={activeModel.numberOfConductors} />
                <StatRow label="Rope Ø (lay-adjusted)" value={`${f2(activeModel.ropeDiameter)} mm`} hint={`packing ${f2(activeModel.ropePackingD)}`} />
                <StatRow label="Lay angle" value={`${activeModel.layAngleDeg.toFixed(1)}°`} />
                <StatRow label="Take-up factor" value={`${activeModel.takeUp.toFixed(4)}×`} hint={`+${((activeModel.takeUp - 1) * 100).toFixed(1)}%`} />
                <hr className="my-2" />
                <StatRow label="Extra copper from twist" value={`+${f2(extraCopper)} kg`} />
                <div className="fs-10 text-700 mt-1">Smaller pas → tighter twist → more conductor consumed per metre of rope.</div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      );
    }

    // sheath or armour layer
    const L = activeModel.cableLayers.find(x => x.key === curPhase);
    if (!L) return null;
    if (L.kind === 'armour') {
      return (
        <Row className="g-3">
          <Col lg={7}><Armour3D layer={L} pitchMm={armourLay} /></Col>
          <Col lg={5}>
            <Card className="h-100">
              <Card.Header className="py-2 fw-bold">Armouring — BLR steel tape</Card.Header>
              <Card.Body className="py-2">
                <Row className="g-2 mb-2">
                  <Col xs={6}>
                    <Form.Label className="fs-10 mb-0">Tape thickness (mm)</Form.Label>
                    <Form.Control size="sm" className="no-spin" placeholder={f2(L.thickness)}
                      value={wallOverrides.armouring?.thin ?? ''} onChange={e => setLayerRadial('armouring', 'thin', e.target.value)} />
                  </Col>
                  <Col xs={6}>
                    <Form.Label className="fs-10 mb-0">Wrap lay (mm)</Form.Label>
                    <Form.Range min={15} max={120} step={5} value={armourLay} onChange={e => setArmourLay(toNum(e.target.value))} />
                  </Col>
                </Row>
                <StatRow label="Tape" value={`${L.wide} × ${f2(L.thickness)} mm`} />
                <StatRow label="Inner Ø" value={`${f2(L.innerD)} mm`} />
                <StatRow label="Outer Ø" value={`${f2(L.outerD)} mm`} />
                <hr className="my-2" />
                <StatRow label="BLR" value={`${f2(activeModel.lines.find(x => x.group === 'BLR')?.kg)} kg`} />
              </Card.Body>
            </Card>
          </Col>
        </Row>
      );
    }
    const kg = (L.ringArea * L.density / 1000) * len;
    return (
      <Row className="g-3">
        <Col lg={7}>
          <SheathLayer3D layer={L} productColor={active.color}
            bedding={L.key === beddingKey && activeModel.hasRope}
            cores={activeModel.cores}
            numberOfConductors={activeModel.numberOfConductors}
            ropeDiameter={activeModel.ropeDiameter}
            interstitial={interstitial} />
        </Col>
        <Col lg={5}>
          <Card className="h-100">
            <Card.Header className="py-2 fw-bold">{PHASE_LABEL[L.key]}</Card.Header>
            <Card.Body className="py-2">
              {L.key === beddingKey && activeModel.hasRope && (() => {
                const nomL = nominalModel.cableLayers.find(x => x.key === beddingKey) || L;
                const sumCores = nominalModel.cores.reduce((s, c) => s + discArea(c.outerD), 0);
                const interRing = Math.max(0, discArea(nomL.outerD) - sumCores);
                const pct = nomL.ringArea > 0 ? (interRing / nomL.ringArea - 1) * 100 : 0;
                return (
                  <div className="mb-2 p-2 rounded bg-warning bg-opacity-10 border border-warning border-opacity-50">
                    <Form.Check type="switch" checked={interstitial}
                      onChange={e => setInterstitial(e.target.checked)}
                      label={<span>Fill voids between conductors <b className="text-danger">+{pct.toFixed(0)}%</b></span>} />
                    <div className="fs-10 text-700 mt-1">
                      This sheath sits directly on the rope. The standard counts only the outer ring; this fills the gaps between the {activeModel.numberOfConductors} conductors (real = disc(this sheath) − {activeModel.numberOfConductors}×conductor area).
                    </div>
                  </div>
                );
              })()}
              <div className="fs-10 text-700 mb-1">Radial wall on each side (mm). Outer Ø, section &amp; consumption update live.</div>
              <SideInputs ov={wallOverrides[L.key]} onSet={(s, v) => setLayerRadial(L.key, s, v)} nominal={f2(L.wall.nominal)} />
              <RealRadialNote layer={L} />
              <DiameterInput label="Outer Ø (mm) — force a value"
                value={wallOverrides[L.key]?.diameter} live={f2(L.outerD)}
                onSet={v => setLayerDiameter(L.key, v)} />
              {(() => {
                const nomL = nominalModel.cableLayers.find(x => x.key === L.key) || L;
                const nomKg = (nomL.ringArea * nomL.density / 1000) * len;
                return (
                  <StatTable rows={[
                    { label: 'Inner Ø (mm)', std: nomL.innerD, cur: L.innerD },
                    { label: 'Wall avg (mm)', std: nomL.wall.avg, cur: L.wall.avg },
                    { label: 'Outer Ø (mm)', std: nomL.outerD, cur: L.outerD },
                    { label: 'Section (mm²)', std: nomL.section, cur: L.section },
                    { label: 'Material area (mm²)', std: nomL.ringArea, cur: L.ringArea },
                    { label: `${L.type || L.material} (kg)`, std: nomKg, cur: kg }
                  ]} />
                );
              })()}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  };

  return (
    <div className="mb-9">
      <h2 className="mb-1">Standard Analytics</h2>
      <p className="text-700">Phase-by-phase 3D geometry, editable walls with live consumption, and product comparison.</p>

      {/* add product */}
      <Card className="mb-3">
        <Card.Body className="py-3">
          <Row className="g-2 align-items-end">
            <Col xs={6} md={2}>
              <Form.Label className="fs-10 mb-0">Cable</Form.Label>
              <Form.Select size="sm" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}>
                {CABLE_NAMES_NO_ROPE.map(n => <option key={n} value={n}>{n}</option>)}
              </Form.Select>
            </Col>
            <Col xs={3} md={1}>
              <Form.Label className="fs-10 mb-0">Cond.</Form.Label>
              <Form.Control size="sm" className="no-spin" value={form.numberOfConductors} onChange={e => setForm({ ...form, numberOfConductors: e.target.value })} />
            </Col>
            <Col xs={3} md={1}>
              <Form.Label className="fs-10 mb-0">Sect.</Form.Label>
              <Form.Control size="sm" className="no-spin" value={form.section} onChange={e => setForm({ ...form, section: e.target.value })} />
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="fs-10 mb-0">GY diff. section</Form.Label>
              <div className="d-flex align-items-center gap-1">
                <Form.Check
                  type="switch"
                  title="Reduced green-yellow (earth) conductor"
                  checked={!!form.mixed}
                  onChange={e => setForm({ ...form, mixed: e.target.checked, gy: e.target.checked ? form.gy : '' })}
                />
                <Form.Control
                  size="sm"
                  className="no-spin"
                  placeholder="V-G mm²"
                  disabled={!form.mixed}
                  value={form.gy}
                  onChange={e => setForm({ ...form, gy: e.target.value })}
                />
              </div>
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="fs-10 mb-0">Color</Form.Label>
              <Form.Select size="sm" value={form.color} onChange={e => setForm({ ...form, color: e.target.value })}>
                {CABLE_COLORS.map(c => <option key={c} value={c}>{c}</option>)}
              </Form.Select>
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="fs-10 mb-0">Length (m)</Form.Label>
              <Form.Control size="sm" className="no-spin" value={form.length} onChange={e => setForm({ ...form, length: e.target.value })} />
            </Col>
            <Col xs={6} md={2}>
              <Button size="sm" variant="primary" onClick={addProduct} disabled={loading}>
                {loading ? <Spinner size="sm" animation="border" className="me-1" /> : <FontAwesomeIcon icon={faPlus} className="me-1" />}
                Add
              </Button>
            </Col>
          </Row>
          {error && <div className="text-danger fs-9 mt-2">{error}</div>}
        </Card.Body>
      </Card>

      {items.length === 0 ? (
        <Alert variant="light" className="text-center text-700">Add a product to start.</Alert>
      ) : (
        <>
          <Nav variant="pills" className="mb-3 gap-2">
            {items.map(it => (
              <Nav.Item key={it.id}>
                <Nav.Link active={it.id === activeId} onClick={() => { setActiveId(it.id); setWallOverrides({}); setInterstitial(false); }} className="py-1 px-3">
                  {it.name} {it.numberOfConductors}x{it.section}
                  {it.std?.greenYellowSection ? `+${it.std.greenYellowSection}` : ''} · {it.color} · {it.length}m
                  <FontAwesomeIcon icon={faTrash} className="ms-2 text-danger" onClick={e => { e.stopPropagation(); removeItem(it.id); }} />
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>

          {active && activeModel && (
            <>
              {/* phase selector */}
              <div className="d-flex gap-2 flex-wrap mb-3">
                {phaseTabs.map(p => (
                  <Button key={p.key} size="sm" variant={curPhase === p.key ? 'primary' : 'outline-secondary'}
                    onClick={() => setSelectedPhase(p.key)}>
                    {p.label}
                  </Button>
                ))}
                <div className="ms-auto d-flex align-items-center gap-2">
                  <span className="fs-10 text-700">Length</span>
                  <Form.Control size="sm" style={{ width: 90 }} className="no-spin" value={active.length}
                    onChange={e => patchItem(active.id, { length: toNum(e.target.value) })} />
                  {hasEdits && (
                    <Button size="sm" variant="outline-warning" onClick={() => { setWallOverrides({}); setInterstitial(false); }}>
                      Reset walls
                    </Button>
                  )}
                </div>
              </div>

              {/* consumption for the working phase: standard vs your walls, with Δ */}
              {nominalModel && activeModel && (() => {
                const std = phaseConsumption(nominalModel, curPhase, active.length);
                const cur = phaseConsumption(activeModel, curPhase, active.length);
                const rows = std.map((s, i) => ({ label: s.label, std: s.kg, cur: cur[i]?.kg ?? s.kg }));
                const tot = rows.reduce((a, r) => ({ std: a.std + r.std, cur: a.cur + r.cur }), { std: 0, cur: 0 });
                const dCell = (s, c) => {
                  const d = c - s;
                  const pct = s > 0 ? (d / s) * 100 : 0;
                  const cls = d > 0.001 ? 'text-danger' : d < -0.001 ? 'text-success' : 'text-700';
                  return <span className={`fw-bold ${cls}`}>{d >= 0 ? '+' : ''}{f2(d)} kg{Math.abs(pct) >= 0.1 ? ` (${d >= 0 ? '+' : ''}${pct.toFixed(1)}%)` : ''}</span>;
                };
                return (
                  <Card className="mb-3 border border-primary border-opacity-50">
                    <Card.Header className="py-2 fw-bold bg-primary bg-opacity-10 d-flex justify-content-between">
                      <span>{PHASE_LABEL[curPhase] || curPhase} — consumption @ {active.length} m</span>
                      {hasEdits && <Badge bg="warning" text="dark">walls edited</Badge>}
                    </Card.Header>
                    {rows.length === 0 ? (
                      <Card.Body className="py-2 fs-9 text-700">No material consumed at this phase (stranding only).</Card.Body>
                    ) : (
                      <Table size="sm" className="fs-9 mb-0 align-middle">
                        <thead>
                          <tr className="text-700">
                            <th>Material</th>
                            <th className="text-end">Standard</th>
                            <th className="text-end">With your walls</th>
                            <th className="text-end">Difference</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map(r => (
                            <tr key={r.label}>
                              <td>{r.label}</td>
                              <td className="text-end">{f2(r.std)} kg</td>
                              <td className="text-end fw-bold">{f2(r.cur)} kg</td>
                              <td className="text-end">{dCell(r.std, r.cur)}</td>
                            </tr>
                          ))}
                          {rows.length > 1 && (
                            <tr className="border-top fw-bold">
                              <td>Phase total</td>
                              <td className="text-end">{f2(tot.std)} kg</td>
                              <td className="text-end">{f2(tot.cur)} kg</td>
                              <td className="text-end">{dCell(tot.std, tot.cur)}</td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    )}
                  </Card>
                );
              })()}

              {renderPhase()}

              {/* all-phases summary: standard vs edited per phase, with Δ */}
              {nominalModel && activeModel && (() => {
                const phaseTotal = (model, key) =>
                  phaseConsumption(model, key, active.length).reduce((s, r) => s + r.kg, 0);
                const rows = phaseTabs.map(p => {
                  const std = phaseTotal(nominalModel, p.key);
                  const cur = phaseTotal(activeModel, p.key);
                  return { key: p.key, label: p.label, std, cur, edited: Math.abs(cur - std) > 0.001 };
                });
                const tStd = rows.reduce((a, r) => a + r.std, 0);
                const tCur = rows.reduce((a, r) => a + r.cur, 0);
                const delta = (s, c) => {
                  const d = c - s;
                  const cls = d > 0.001 ? 'text-danger fw-bold' : d < -0.001 ? 'text-success fw-bold' : 'text-700';
                  return <span className={cls}>{d >= 0 ? '+' : ''}{f2(d)}{Math.abs(s) > 0 && Math.abs(d) > 0.001 ? ` (${d >= 0 ? '+' : ''}${((d / s) * 100).toFixed(1)}%)` : ''}</span>;
                };
                return (
                  <Card className="mt-3">
                    <Card.Header className="py-2 fw-bold d-flex justify-content-between">
                      <span>Summary — all phases @ {active.length} m</span>
                      {hasEdits && <Badge bg="danger">edited phases in red</Badge>}
                    </Card.Header>
                    <div className="table-responsive">
                      <Table size="sm" className="fs-9 mb-0 align-middle">
                        <thead>
                          <tr className="text-700">
                            <th>Phase</th>
                            <th className="text-end">Standard (kg)</th>
                            <th className="text-end">With edits (kg)</th>
                            <th className="text-end">Δ (kg)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map(r => (
                            <tr key={r.key} className={r.edited ? 'table-danger' : ''}>
                              <td className={r.edited ? 'fw-bold' : ''}>
                                {r.label}{r.edited && <Badge bg="danger" className="ms-2">modified</Badge>}
                                {r.key === curPhase && <span className="text-primary ms-1">◀ working</span>}
                              </td>
                              <td className="text-end">{r.std > 0 ? `${f2(r.std)}` : '—'}</td>
                              <td className={`text-end ${r.edited ? 'fw-bold' : ''}`}>{r.cur > 0 ? `${f2(r.cur)}` : '—'}</td>
                              <td className="text-end">{r.std > 0 || r.cur > 0 ? delta(r.std, r.cur) : '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="border-top fw-bold">
                            <td>Total</td>
                            <td className="text-end">{f2(tStd)} kg</td>
                            <td className="text-end">{f2(tCur)} kg</td>
                            <td className="text-end">{delta(tStd, tCur)}</td>
                          </tr>
                        </tfoot>
                      </Table>
                    </div>
                  </Card>
                );
              })()}

              {/* full cable totals (with standard→edited delta when walls are edited) */}
              {(() => {
                const dSpan = (s, c) => {
                  if (!hasEdits) return null;
                  const d = c - s;
                  if (Math.abs(d) < 0.01) return null;
                  const cls = d > 0 ? 'text-danger' : 'text-success';
                  return <span className={`ms-1 ${cls}`}>({d >= 0 ? '+' : ''}{f2(d)})</span>;
                };
                const n = nominalModel?.totals || activeModel.totals;
                return (
                  <Card className="mt-3">
                    <Card.Body className="py-2 d-flex gap-4 flex-wrap fs-9 align-items-center">
                      <span className="fw-bold">Full cable @ {active.length} m{hasEdits ? ' (standard → your walls)' : ''}</span>
                      <span>Cu <b>{f2(activeModel.totals.copperKg)}</b> kg{dSpan(n.copperKg, activeModel.totals.copperKg)}</span>
                      <span>PVC <b>{f2(activeModel.totals.pvcKg)}</b> kg{dSpan(n.pvcKg, activeModel.totals.pvcKg)}</span>
                      {activeModel.totals.blrKg > 0 && <span>BLR <b>{f2(activeModel.totals.blrKg)}</b> kg{dSpan(n.blrKg, activeModel.totals.blrKg)}</span>}
                      <span>Total <b>{f2(activeModel.totals.totalKg)}</b> kg{dSpan(n.totalKg, activeModel.totals.totalKg)}</span>
                      <span className="text-700">outer Ø {f2(activeModel.outerDiameter)} mm{hasEdits ? ` (std ${f2(nominalModel.outerDiameter)})` : ''}</span>
                    </Card.Body>
                  </Card>
                );
              })()}
            </>
          )}

          {/* comparison */}
          <Card className="mt-4">
            <Card.Header className="py-2 fw-bold">Comparison — {items.length} product(s)</Card.Header>
            <Card.Body>
              <div className="table-responsive mb-3">
                <Table size="sm" className="fs-9 align-middle mb-0">
                  <thead>
                    <tr className="text-700">
                      <th>Product</th><th className="text-end">Length</th><th className="text-end">Outer Ø</th>
                      <th className="text-end">take-up</th><th className="text-end">Cu kg</th>
                      <th className="text-end">PVC kg</th><th className="text-end">BLR kg</th><th className="text-end">Total kg</th>
                    </tr>
                  </thead>
                  <tbody>
                    {compareRows.map(({ it, m }) => (
                      <tr key={it.id} className={it.id === activeId ? 'table-active' : ''}>
                        <td className="fw-semibold">{it.name} {it.numberOfConductors}x{it.section}{it.std?.greenYellowSection ? `+${it.std.greenYellowSection}` : ''} <Badge bg="secondary">{it.color}</Badge></td>
                        <td className="text-end">{it.length} m</td>
                        <td className="text-end">{f2(m.outerDiameter)}</td>
                        <td className="text-end">{m.takeUp.toFixed(3)}×</td>
                        <td className="text-end">{f2(m.totals.copperKg)}</td>
                        <td className="text-end">{f2(m.totals.pvcKg)}</td>
                        <td className="text-end">{m.totals.blrKg > 0 ? f2(m.totals.blrKg) : '—'}</td>
                        <td className="text-end fw-bold">{f2(m.totals.totalKg)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              {Object.keys(compareChartData).length > 0 && (
                <MaterialTotalsChart data={compareChartData} title="Total material per product" unit="kg" />
              )}
            </Card.Body>
          </Card>
        </>
      )}
    </div>
  );
};

export default StandardAnalytics;
